import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import SelectField from 'components/select-field';
import { ListingEntry } from 'graphql/listings/queries';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useUserInformationContext } from 'contexts/userContext';

import { Fields } from './modal-create-orders-hooks';

const useStyles = makeStyles((theme) => ({
  container: { overflow: 'hidden' },
  title: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  buttonsContainer: { marginBottom: theme.spacing(1) },
  createButtonContainer: { marginLeft: theme.spacing(2) },
  buttonText: {
    textTransform: 'capitalize',
    color: theme.palette.primary.main,
  },
}));

type ModalCreateOrdersSceneProps = {
  t: TFunction;
  open: boolean;
  methods: UseFormReturn<Fields, any>;
  transactionTypes: ListingEntry[];
  types: ListingEntry[];
  workflows: ListingEntry[];
  onClose: () => void;
  onCancel: () => void;
  selectTransactionType: (code: string) => void;
  handleOnSubmit: (data: Fields) => void;
};

const ModalCreateOrdersScene: FC<ModalCreateOrdersSceneProps> = ({
  t,
  open,
  methods,
  transactionTypes,
  types,
  workflows,
  onClose,
  onCancel,
  selectTransactionType,
  handleOnSubmit,
}) => {
  const classes = useStyles();
  const { currentUser } = useUserInformationContext();

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogContent className={classes.container}>
        <Grid container direction="column" spacing={2}>

          <Grid item xs={12}>
            <Typography>
              {t('orders:create-order-leme')}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <form onSubmit={methods.handleSubmit(handleOnSubmit)}>
              <Grid container direction="row" spacing={3}>
                <Grid item xs={6}>
                  <Controller
                    name="transactionType"
                    control={methods.control}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <SelectField
                        {...field}
                        inputRef={ref}
                        data={transactionTypes}
                        dataKey="code"
                        dataText="description"
                        dataValue="code"
                        emptyValue={false}
                        label={t('common:transaction-types')}
                        handleChange={(event) => {
                          selectTransactionType(event.target.value);
                          onChange(event);
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="type"
                    control={methods.control}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <SelectField
                        {...field}
                        inputRef={ref}
                        data={types}
                        dataKey="code"
                        dataText="description"
                        dataValue="code"
                        emptyValue={false}
                        disabled={types.length === 0}
                        label={t('common:type')}
                        handleChange={onChange}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="workflow"
                    control={methods.control}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <SelectField
                        {...field}
                        inputRef={ref}
                        data={workflows}
                        dataKey="code"
                        dataText="description"
                        dataValue="code"
                        emptyValue={false}
                        disabled={workflows.length === 0}
                        label={t('common:workflow')}
                        handleChange={onChange}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="agencyId"
                    control={methods.control}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <SelectField
                        {...field}
                        inputRef={ref}
                        data={currentUser?.accesses ?? []}
                        dataKey="_id"
                        dataText="name"
                        dataValue="_id"
                        emptyValue={false}
                        label={t('common:agency')}
                        handleChange={onChange}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} className={classes.buttonsContainer}>
                  <Grid container direction="row" justifyContent="flex-end">
                    <Grid item>
                      <Button className={classes.buttonText} onClick={onCancel}>
                        {t('common:cancel')}
                      </Button>
                    </Grid>

                    <Grid item className={classes.createButtonContainer}>
                      <Button
                        disableElevation
                        type="submit"
                        variant="contained"
                        color="secondary"
                        className={classes.buttonText}
                      >
                        {t('orders:create-order')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ModalCreateOrdersScene;
